import { useEffect, useState } from "react";
import { adminListUsers, adminUpdateUserRole, adminBanUser, adminDeleteUser, fetchRoles, adminSetRolePermissions } from "@/lib/api";
import { toast } from "sonner";
import { Ban, Trash2, ShieldCheck, Undo2 } from "lucide-react";

const ROLE_LABELS = {
  admin: "Admin", content_editor: "Content Editor", moderator: "Moderator",
  guest_writer: "Guest Writer", ambassador: "Ambassador", user: "Member",
};
const PERM_LABELS = {
  "content.create": "Create content", "content.edit": "Edit content",
  "content.publish": "Publish content", "content.delete": "Delete content",
  "community.moderate": "Moderate community", "users.manage": "Manage users",
  "site.manage": "Manage site settings", "media.manage": "Manage media",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(["admin", "content_editor", "moderator", "guest_writer", "ambassador", "user"]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("users");

  const load = () => {
    fetchRoles().then((d) => { setRoles(d.roles); setPermissions(d.permissions || []); setMatrix(d.matrix || {}); }).catch(() => {});
    adminListUsers().then(setUsers).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const change = async (u, role) => {
    const prev = u.role;
    setUsers((p) => p.map((x) => x.id === u.id ? { ...x, role } : x));
    try { await adminUpdateUserRole(u.id, role); toast(`${u.name || u.email} → ${ROLE_LABELS[role] || role}`); }
    catch (err) { setUsers((p) => p.map((x) => x.id === u.id ? { ...x, role: prev } : x)); toast.error("Could not update role", { description: err.response?.data?.detail || err.message }); }
  };

  const toggleBan = async (u) => {
    const next = !u.banned;
    try { await adminBanUser(u.id, next); setUsers((p) => p.map((x) => x.id === u.id ? { ...x, banned: next } : x)); toast(next ? `${u.name || u.email} suspended` : `${u.name || u.email} reinstated`); }
    catch (err) { toast.error("Action failed", { description: err.response?.data?.detail || err.message }); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? This cannot be undone.`)) return;
    try { await adminDeleteUser(u.id); setUsers((p) => p.filter((x) => x.id !== u.id)); toast(`${u.name || u.email} deleted`); }
    catch (err) { toast.error("Delete failed", { description: err.response?.data?.detail || err.message }); }
  };

  const togglePerm = async (role, perm) => {
    const cur = matrix[role] || [];
    const next = cur.includes(perm) ? cur.filter((p) => p !== perm) : [...cur, perm];
    setMatrix((m) => ({ ...m, [role]: next }));
    try { await adminSetRolePermissions(role, next); }
    catch (err) { setMatrix((m) => ({ ...m, [role]: cur })); toast.error("Could not save permission", { description: err.response?.data?.detail || err.message }); }
  };

  return (
    <div className="max-w-5xl">
      <h1 className="mb-1 font-serif-latin text-3xl text-ink">Users & Roles</h1>
      <p className="mb-6 text-sm text-ink-3">Assign roles, suspend or remove members, and control what each role can access.</p>

      <div className="mb-6 flex gap-1 border-b border-line">
        {[["users", "Members"], ["permissions", "Role Permissions"]].map(([k, l]) => (
          <button key={k} data-testid={`users-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === k ? "border-saffron text-ink" : "border-transparent text-ink-3 hover:text-ink-2"}`}>{l}</button>
        ))}
      </div>

      {tab === "users" && (loading ? <p className="py-16 text-center text-ink-3">…</p> : (
        <div className="overflow-hidden border border-line bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-surface-alt/50 text-xs uppercase tracking-wider text-ink-3"><tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className={`border-b border-line last:border-0 ${u.banned ? "bg-red-50/40" : ""}`}>
                  <td className="px-4 py-3"><span className="text-ink">{u.name || "—"}</span><span className="block text-xs text-ink-3">{u.email}</span></td>
                  <td className="px-4 py-3">
                    <select data-testid={`user-role-${u.email}`} value={u.role || "user"} onChange={(e) => change(u, e.target.value)} className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none">
                      {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {u.banned
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">Suspended</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">Active</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`user-ban-${u.email}`} onClick={() => toggleBan(u)} title={u.banned ? "Reinstate" : "Suspend"}
                        className="inline-flex items-center gap-1 border border-line px-2.5 py-1.5 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                        {u.banned ? <><Undo2 className="h-3.5 w-3.5" /> Reinstate</> : <><Ban className="h-3.5 w-3.5" /> Suspend</>}
                      </button>
                      <button data-testid={`user-delete-${u.email}`} onClick={() => remove(u)} title="Delete"
                        className="inline-flex items-center gap-1 border border-line px-2.5 py-1.5 text-xs text-red-600 transition-colors hover:border-red-500 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={4} className="px-4 py-16 text-center text-ink-3">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ))}

      {tab === "permissions" && (
        <div className="overflow-x-auto border border-line bg-surface">
          <div className="mb-0 flex items-center gap-2 border-b border-line px-4 py-3 text-sm text-ink-2">
            <ShieldCheck className="h-4 w-4 text-saffron" /> Admin always has full access. Tick what each other role can do.
          </div>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-surface-alt/50 text-xs uppercase tracking-wider text-ink-3">
              <tr>
                <th className="px-4 py-3">Permission</th>
                {roles.filter((r) => r !== "admin").map((r) => <th key={r} className="px-3 py-3 text-center">{ROLE_LABELS[r] || r}</th>)}
              </tr>
            </thead>
            <tbody>
              {permissions.map((perm) => (
                <tr key={perm} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 text-ink">{PERM_LABELS[perm] || perm}</td>
                  {roles.filter((r) => r !== "admin").map((r) => (
                    <td key={r} className="px-3 py-3 text-center">
                      <input type="checkbox" data-testid={`perm-${r}-${perm}`} checked={(matrix[r] || []).includes(perm)} onChange={() => togglePerm(r, perm)} className="h-4 w-4 accent-[var(--saffron)]" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
